import Vue from 'vue'
import { Client } from '@/api/client'
import { Restaurant } from '@/api'
import { Storage } from '@/modules/plugins/storage'
import { isValidNumber } from 'libphonenumber-js'
import { prototype, reset } from './prototype'
import Enhancer from '../Enhancer'
import store from '@/modules/store'
import router from '@/modules/router'
import mixins from '@/modules/mixins/global'
import EventEmitter from 'events'
import isEmpty from 'lodash.isempty'
import isEqual from 'lodash.isequal'
import cloneDeep from 'lodash.clonedeep'
import throttle from 'lodash.throttle'

const NEUTRAL_EXPENSE_CODE_FLAGS = Object.freeze({
    hasEmptyRow: false,
    isSaved: true,
    isValid: true,
    isDirty: false,
    distributionError: null,
})

class Cart {
    constructor() {
        this.apiRoot = 'api'
        this.apiVersion = 'v1'
        this._client = new Client()
        this._event = new EventEmitter()
        this._model = {}
        this._calculation = {}
        this._temporary = null
        this._inactiveItems = []
        this._loading = false
        this._delay = false
        this._timeout = null
        this._deliveryTimeAdjustment = null
        // tasks#170: active-cart calculates are numbered; only the latest one applies its answer
        this._calculateSequence = 0
        // the latest calculate's outcome ({ sequence, promise, resolve, reject }) superseded calls wait for
        this._latestCalculate = null
        // the newest answer of a superseded calculate ({ sequence, result }) and the sequence whose answer is applied
        this._supersededAnswer = null
        this._appliedSequence = 0
        // sequence of the latest calculate when it ended without an answer (error or no result)
        this._failedSequence = 0
        // tasks#170: a "moved" time not told yet, and the number of mounted cart watchers that tell it
        this._movedNotice = null
        this._movedNoticeHandlers = 0
        // tasks#170: mounted "delivery time changed" alerts (ModalDeliveryStateChange: restaurant and checkout pages)
        this._deliveryTimeAlerts = 0
        this._expenseCodeErrors = { ...NEUTRAL_EXPENSE_CODE_FLAGS }
        // tasks#207: the expense code editor (component uid) whose flags the cart holds; only the active one claims
        this._expenseCodeOwner = null
        this.model = Storage.getItem('cart') || JSON.parse(JSON.stringify(prototype))
        return new Proxy(this, Enhancer)
    }

    get base() {
        return `${this.apiRoot}/${this.apiVersion}`
    }

    get loading() {
        return this._loading
    }

    get delay() {
        return this._delay
    }

    // what the last calculate of the active cart did with the selected time: 'moved' (a scheduled time moved
    // forward, tasks#168), 'asap' (an invalid scheduled time replaced by ASAP) or null (kept)
    get deliveryTimeAdjustment() {
        return this._deliveryTimeAdjustment
    }

    //Cart model
    get model() {
        return this._model || {}
    }

    set model(val) {
        this._model = val || {}
    }

    async resetCart() {
        this.calculation = {}
        Storage.removeItem('cart')
        this.model = {
            ...this.model,
            ...JSON.parse(JSON.stringify(reset)),
        }
        await store.dispatch('order/resetRestaurant')
        await store.dispatch('order/resetGroup')
        await store.dispatch('resetAllowance')
        return
    }

    setPhone(user, address = {}) {
        if (!user) return
        this._model.phone = undefined
        this._model.phone_is_valid = undefined
        if (address.is_company_address) {
            if (user.phone) this._model.phone = user.phone
        } else {
            if (address.phone) this._model.phone = address.phone
            else if (!address.phone && user.phone) this._model.phone = user.phone
        }
        if (this._model.phone)
            this._model.phone_is_valid = isValidNumber(
                this._model.phone,
                store.getters['site/phoneFormat']
            )
    }

    setInvoice(user) {
        this._model.billing_name = user?.last_billing_info?.name || undefined
        this._model.billing_vatnumber = user?.last_billing_info?.vatnumber || undefined
        this._model.billing_full_address = user?.last_billing_info?.full_address || undefined
        this._model.billing_sub_address = user?.last_billing_info?.sub_address || undefined
    }

    resetAddress() {
        this._model.address = undefined
        this._model.delivery_full_address = undefined
        this._model.longitude = undefined
        this._model.latitude = undefined
        this._model.delivery_sub_address = undefined
        this._model.comment = undefined
    }

    isAddressEqual(address) {
        return (
            this._model.delivery_full_address === address.full_address &&
            this._model.longitude === address.longitude &&
            this._model.latitude === address.latitude &&
            this._model.delivery_sub_address === address.sub_address &&
            this._model.comment === address.comment
        )
    }

    get hasAddress() {
        if (this.isResidentialCanteen) {
            return store.getters['active/canteen']?.user_address
                ? !!this._model.address
                : !!this._model.delivery_full_address
        } else {
            return Vue.prototype.$user.isLoggedIn
                ? !!this._model.address
                : !!this._model.delivery_full_address
        }
    }

    //Cart setters
    set selected_expense_rule(val) {
        this._model.selected_expense_rule =
            val && val.id ? `/${this.base}/company-expense-rules/${val.id}` : null
    }

    set address(val) {
        this.resetAddress()
        this._model.address = this.isResidentialCanteen
            ? val?.user_address
            : val?.id
            ? `/api/v1/user-addresses/${val.id}`
            : undefined
        this._model.delivery_full_address = val?.full_address || undefined
        this._model.longitude = val?.longitude || undefined
        this._model.latitude = val?.latitude || undefined
        this._model.delivery_sub_address = val?.sub_address || undefined
        if (val?.comment) this._model.comment = val?.comment
    }

    set canteen(val) {
        if (val?.isResidential && val?.id) {
            this.model.residentialcanteen_id = val.id
            this.model.cloudcanteen_id = undefined
        } else if (val?.id) {
            this.model.residentialcanteen_id = undefined
            this.model.cloudcanteen_id = val.id
        } else {
            this.model.residentialcanteen_id = undefined
            this.model.cloudcanteen_id = undefined
        }
    }

    set user($user) {
        this._model.delivery_name = $user?.isLoggedIn ? $user.name : null
    }

    //Get meals
    get meals() {
        return this._model.items || []
    }

    get isEmpty() {
        return isEmpty(this._model.items)
    }

    //Set meals

    set meals(val) {
        this._model.items = val
    }

    pushMeal(val) {
        this._model.items.push(val)
    }

    removeMeal(index) {
        this._model.items.splice(index, 1)
    }

    setMeal(val, index) {
        Vue.set(this._model.items, index, val)
    }

    addMeal(index, quantity = 1) {
        Vue.set(this._model.items[index], 'quantity', this._model.items[index].quantity + quantity)
    }

    removeInactiveMeals() {
        this._model.items = this.items.filter(
            (item) =>
                !this.inactiveItems.some(
                    (inactiveItem) => mixins.getId(item.meal) === inactiveItem?.id
                )
        )
    }

    resetMeals() {
        this._model.items = []
    }

    setMealMinQuantity(val, index) {
        Vue.set(this._model.items[index], 'minimum_quantity', val)
    }

    verifyMeals() {
        this._model.items = this._model.items.reduce((items, item) => {
            if (item) {
                item.children = item.children.reduce((children, child) => {
                    if (child) children.push(child)
                    return children
                }, [])
                items.push(item)
            }
            return items
        }, [])
    }

    hasItemFromOtherRestaurants(restaurantId) {
        return restaurantId !== mixins.getId(this.restaurant) && !!this._model.items.length
    }

    //Inactive items
    get inactiveItems() {
        return this._inactiveItems || []
    }

    set inactiveItems(validationErrors) {
        const result = new Array(this._model.items.length).fill(null)
        validationErrors
            .filter((error) => ['items.meal'].includes(error.propertyPath?.replace(/\[\d+]/, '')))
            .forEach((mealError) => {
                const index = parseInt(mealError.propertyPath.match(/(\d+)/)?.[0] || '-1')
                if (index > -1)
                    result[index] = {
                        id: mixins.getId(this._model.items[index].meal),
                        message: mealError.message,
                    }
            })
        this._inactiveItems = result
    }

    get hasInactiveItem() {
        return !!this.inactiveItems.find((item) => !!item)
    }

    //Cart calculation
    get calculated() {
        return !isEmpty(this._calculation)
    }

    get calculation() {
        return this._calculation || {}
    }

    set calculation(val) {
        this._calculation = val || {}
    }

    get temporary() {
        return this._temporary
    }

    set temporary(val) {
        this._temporary = val
    }

    get hasTemporary() {
        return !isEmpty(this._temporary)
    }

    resetTemporary() {
        this._temporary = null
    }

    //Expense rules
    get validExpenseRule() {
        return this.calculated &&
            this.calculation.usable_expense_rules.length &&
            this.calculation.selected_expense_rule
            ? Boolean(
                  this.calculation.usable_expense_rules.find(
                      (rule) => rule.id === this.calculation.selected_expense_rule.id
                  )
              )
            : false
    }

    get hasExpenseRule() {
        return !!this._model.selected_expense_rule
    }

    get expenseCodeHasEmptyRow() {
        return this._expenseCodeErrors.hasEmptyRow
    }

    get expenseCodeIsSaved() {
        return this._expenseCodeErrors.isSaved
    }

    get expenseCodeIsValid() {
        return this._expenseCodeErrors.isValid
    }

    get expenseCodeIsDirty() {
        return this._expenseCodeErrors.isDirty
    }

    get expenseCodeDistributionError() {
        return this._expenseCodeErrors.distributionError
    }

    // tasks#207: one expense code editor at a time owns the flags; without an owner they are neutral
    claimExpenseCodeFlags(uid, flags) {
        this._expenseCodeOwner = uid
        this._setExpenseCodeFlags(flags)
    }

    publishExpenseCodeFlags(uid, flags) {
        if (this._expenseCodeOwner === uid) this._setExpenseCodeFlags(flags)
    }

    releaseExpenseCodeFlags(uid) {
        if (this._expenseCodeOwner !== uid) return
        this._expenseCodeOwner = null
        this._setExpenseCodeFlags(NEUTRAL_EXPENSE_CODE_FLAGS)
    }

    _setExpenseCodeFlags(flags) {
        Object.keys(NEUTRAL_EXPENSE_CODE_FLAGS).forEach((key) => {
            this._expenseCodeErrors[key] = flags[key]
        })
    }

    // Group or Canteen order
    get isResidentialCanteen() {
        return !!this.model.residentialcanteen_id || !!this.calculation.residentialcanteen_id
    }

    get isCloudCanteen() {
        return !!this.model.cloudcanteen_id || !!this.calculation.cloudcanteen_id
    }

    get isCanteen() {
        return this.isCloudCanteen || this.isResidentialCanteen
    }

    get canCanteenGroupOrder() {
        return this.calculated
            ? Boolean(
                  this.calculation.cloudcanteen_id &&
                      this.calculation.cloudcanteen.can_group_order &&
                      this.calculation.cloudcanteen.group_available_balances
              )
            : false
    }

    get canStandardGroupOrder() {
        return this.calculated && this.validExpenseRule
            ? this.calculation.selected_expense_rule.can_group_order &&
                  this.calculation.selected_expense_rule.group_available_balances
            : false
    }

    get groupOrderBalances() {
        return this.canStandardGroupOrder
            ? this.calculation.selected_expense_rule.group_available_balances
            : this.canCanteenGroupOrder
            ? this.calculation.cloudcanteen.group_available_balances
            : {}
    }

    get groupOrderNames() {
        return this.canStandardGroupOrder
            ? this.calculation.selected_expense_rule.group_available_names || {}
            : this.canCanteenGroupOrder
            ? this.calculation.cloudcanteen.group_available_names || {}
            : {}
    }

    get groupOrderPersonalBudget() {
        return this.canStandardGroupOrder
            ? this.calculation.selected_expense_rule.budget
            : this.canCanteenGroupOrder
            ? this.calculation.cloudcanteen.budget
            : 0
    }

    get groupIsCreated() {
        return this.calculated
            ? this.calculation.group_order_employees.length > 0 ||
                  !isEmpty(this.model.group_order_sum_distribution)
            : false
    }

    get isCanteenGroupOrder() {
        return this.canCanteenGroupOrder && this.groupIsCreated
    }

    get isStandardGroupOrder() {
        return this.canStandardGroupOrder && this.groupIsCreated
    }

    get isGroupOrder() {
        return this.isStandardGroupOrder || this.isCanteenGroupOrder
    }

    get isGroupSaved() {
        return this.calculated
            ? isEqual(this.model.group_order_employees, this.calculation.group_order_employees)
            : false
    }

    resetGroupOrder() {
        return new Promise((resolve, reject) => {
            store
                .dispatch('order/resetGroup')
                .then(() => {
                    this.model.group_order_employees = []
                    this.model.group_order_sum_distribution = {}
                    resolve()
                })
                .catch((e) => {
                    reject(e)
                })
        })
    }

    resetAllowance() {
        return new Promise((resolve, reject) => {
            store
                .dispatch('resetAllowance')
                .then(() => {
                    this.selected_expense_rule = null
                    this.expense_code = null
                    resolve()
                })
                .catch((e) => {
                    reject(e)
                })
        })
    }

    overviewCart() {
        return new Promise((resolve, reject) => {
            const needResetGroup =
                (!this.groupIsCreated && !isEmpty(store.getters['order/groupSettings'])) ||
                (this.groupIsCreated &&
                    !this.model.selected_expense_rule &&
                    !this.model.cloudcanteen_id)
            const needResetAllowance =
                router.currentRoute.name === 'checkout' &&
                !this.validExpenseRule &&
                !!this.model.selected_expense_rule

            if (this.meals.length) {
                this.meals.forEach((meal, index) => {
                    if (
                        meal.minimum_quantity !== this.calculation.items[index].minimum_quantity &&
                        this.calculation.items[index].quantity <
                            this.calculation.items[index].minimum_quantity
                    ) {
                        this.setMealMinQuantity(this.calculation.items.minimum_quantity, index)
                    }
                })
            }
            if (needResetGroup || needResetAllowance) {
                const reset = needResetAllowance
                    ? cloneDeep(this.calculation.selected_expense_rule)
                    : null
                Promise.all([
                    needResetGroup ? this.resetGroupOrder() : undefined,
                    needResetAllowance ? this.resetAllowance() : undefined,
                ])
                    .then(() => {
                        reject(reset)
                    })
                    .catch(() => {
                        reject()
                    })
            } else {
                resolve()
            }
        })
    }

    async autoPaymentMethod() {
        try {
            if (
                !this.calculation.selected_expense_rule && // expense rule not selected
                !this.calculation.payment_method && // payment not selected
                this.calculation.usable_expense_rules.length === 1 // only one available expense rule
            ) {
                await store.dispatch('setAllowance', this.calculation.usable_expense_rules[0])
                await this.calculate()
            } else if (
                !this.calculation.selected_expense_rule && // expense rule not selected
                !this.calculation.payment_method && // payment not selected
                this.calculation.payment_methods?.length === 1 && // only one available payment method
                this.calculation.usable_expense_rules.length === 0 // no available expense rule
            ) {
                this._model.payment_method = this.calculation.payment_methods[0]
                await this.calculate()
            } else if (
                (this.calculation.selected_expense_rule || this.calculation.cloudcanteen_id) && // has expense rule or CC order
                this.calculation.price_sum_user > 0 && // need complementary payment
                !this.calculation.payment_method && // doesn't have complementary payment
                this.calculation.payment_methods_with_expense_rule.length === 1 // only one payment method available
            ) {
                this._model.payment_method = this.calculation.payment_methods_with_expense_rule[0]
                await this.calculate()
            } else if (
                (this.calculation.selected_expense_rule || this.calculation.cloudcanteen_id) && // has expense rule or CC order
                this.calculation.price_sum_user === 0 && // doesn't need complementary payment
                this.calculation.payment_method // has complementary payment
            ) {
                this._model.payment_method = null
                await this.calculate()
            }
            return Promise.resolve()
        } catch (e) {
            return Promise.reject(e)
        }
    }

    throttleCalculate = throttle(async () => {
        await this.calculate()
    }, 60000)

    calculateFinished() {
        this._loading = false
        this._delay = false
        if (this._timeout) {
            clearTimeout(this._timeout)
            this._timeout = null
        }
    }

    async checkError(e) {
        if (e?.res?.status === 400) {
            if (e?.body?.detail?.includes('user-addresses')) {
                await Vue.prototype.$addresses.initAddresses()
                await store.dispatch('order/resetAddress')
                this.throttleCalculate()
            } else if (e?.body?.detail?.includes('restaurants')) {
                await store.dispatch('order/resetRestaurant')
                this._model.restaurant = null
                this.throttleCalculate()
            } else {
                try {
                    const response = await this.checkItems()
                    if (response.removed) this.throttleCalculate()
                } finally {
                    this.calculateFinished()
                }
            }
        } else {
            this.calculateFinished()
        }
        // eslint-disable-next-line no-console
        console.error(e)
    }

    async calculate(restaurant, log = false) {
        // tasks#170: this call's number among the active-cart calculates (null: temporary, or never sent)
        let sequence = null
        let outcome = null
        try {
            this.calculateDelay()

            let canteen = store.getters['active/canteen']

            await this.canteenAndAddressCheck(canteen)
            this.setPropertiesBeforeCalculate(canteen)
            if (this.onNoSelectedRestaurant()) return
            window.debug.info(`Calculate order`)

            const model = cloneDeep(this.model)
            const temporaryRestaurant = restaurant || this.temporary?.restaurant

            if (temporaryRestaurant) {
                delete model.items
                model.restaurant = temporaryRestaurant
            }
            if (Vue.prototype.$user.isLoggedIn && !isEmpty(canteen)) delete model.comment
            if (router.currentRoute.name !== 'checkout') {
                delete model.selected_expense_rule
                delete model.expense_code
                delete model.group_order_employees
                delete model.group_order_sum_distribution
            }

            // tasks#170: only the latest calculate of the active cart applies its answer. A slower older one (e.g. the
            // ASAP calculate of the page load, answered after the customer or the checkout picked a time) would
            // otherwise overwrite the newer calculation and turn the newer selected time back into ASAP. A superseded
            // call resolves / rejects with the latest call's outcome, once the cart holds it: its caller (placeOrder,
            // autoPaymentMethod, the coupon modal, ...) always continues on the answer of its own request or a newer one
            if (!temporaryRestaurant) {
                sequence = ++this._calculateSequence
                outcome = this.startLatestCalculate(sequence)
            }
            let result
            try {
                result = await this._client.store(
                    `${this.base}/calculate${log ? '?log=1' : ''}`,
                    model
                )
            } catch (e) {
                // superseded: no error handling here (it would clear the loading flag of the newer calculate or
                // reset the address), the latest call decides
                if (this.isSuperseded(sequence)) return this.supersededOutcome(outcome)
                throw e
            }
            Storage.setItem('cart', this.model)
            if (this.isSuperseded(sequence)) {
                this.keepSupersededAnswer(sequence, result)
                return this.supersededOutcome(outcome)
            }
            this.onInvalidDeliveryTime(result, temporaryRestaurant)
            if (sequence !== null && !result) {
                // no answer from the latest call: the newest successful superseded answer (if any) is the best we have
                this._failedSequence = sequence
                this.applySupersededAnswer()
            } else {
                if (sequence !== null) this._appliedSequence = sequence
                this.storeResult(result, temporaryRestaurant)
            }
            this.calculateFinished()
            if (outcome) outcome.resolve(result)
            return result
        } catch (e) {
            if (sequence !== null && !this.isSuperseded(sequence)) {
                this._failedSequence = sequence
                this.applySupersededAnswer()
            }
            try {
                await this.checkError(e)
            } finally {
                // settled even when the error handling itself fails: superseded callers must never wait forever
                if (outcome) outcome.reject(e)
            }
            throw e
        }
    }

    // tasks#170: a newer active-cart calculate was started after this one
    isSuperseded(sequence) {
        return sequence !== null && sequence !== this._calculateSequence
    }

    startLatestCalculate(sequence) {
        const outcome = { sequence }
        outcome.promise = new Promise((resolve, reject) => {
            outcome.resolve = resolve
            outcome.reject = reject
        })
        // only superseded calls observe it
        outcome.promise.catch(() => {})
        this._latestCalculate = outcome
        return outcome
    }

    // resolves / rejects with the outcome of the latest calculate, following it while newer ones start
    async supersededOutcome(ownOutcome) {
        // calls waiting on this (no longer latest) call move on to the latest one
        if (ownOutcome) ownOutcome.resolve(undefined)
        for (;;) {
            const latest = this._latestCalculate
            try {
                const result = await latest.promise
                if (latest === this._latestCalculate) return result
            } catch (e) {
                if (latest === this._latestCalculate) throw e
            }
        }
    }

    keepSupersededAnswer(sequence, result) {
        if (!result || (this._supersededAnswer && this._supersededAnswer.sequence > sequence))
            return
        this._supersededAnswer = { sequence, result }
        // the latest call already ended without an answer
        if (this._failedSequence === this._calculateSequence) this.applySupersededAnswer()
    }

    // the latest call gave no answer: show the newest successful older one instead of the calculation from before
    // both (prices, items); its time handling (onInvalidDeliveryTime) is not applied, it judged an older time
    applySupersededAnswer() {
        const kept = this._supersededAnswer
        if (!kept || kept.sequence <= this._appliedSequence) return
        this._appliedSequence = kept.sequence
        this.storeResult(kept.result, null)
    }

    calculateDelay() {
        this._loading = true
        if (this._timeout) clearTimeout(this._timeout)
        this._timeout = setTimeout(() => {
            this._delay = true
        }, 1000)
    }

    setPropertiesBeforeCalculate(canteen) {
        this.user = Vue.prototype.$user
        this.selected_expense_rule = store.getters['allowance']
        this.model.required_delivery_time = isEmpty(canteen)
            ? store.getters['order/time']
                ? Vue.moment(store.getters['order/time']).format()
                : undefined
            : canteen.delivery_time
    }

    async canteenAndAddressCheck(canteen) {
        const missingAddress =
            !this.model.delivery_full_address || !this.model.delivery_full_address?.length
        if (
            (isEmpty(canteen) || !canteen.isResidential || missingAddress) &&
            Vue.prototype.$user.isLoggedIn &&
            !Vue.prototype.$addresses.total
        ) {
            await Vue.prototype.$addresses.initAddresses()
        }

        if (isEmpty(canteen) && (this.isResidentialCanteen || this.isCloudCanteen))
            this.canteen = null
        // precautions to prevent that strange delivery_full_address loosing bug
        // TODO: further investigation if more information received
        if (missingAddress && canteen?.id) {
            this.address = canteen.isResidential
                ? canteen.address
                : Vue.prototype.$addresses.findItem(mixins.getId(canteen.address))
        } else if (missingAddress && (this.model.address || store.getters['order/address']?.id)) {
            const address = await Vue.prototype.$addresses.findAddress(
                this.model.address || store.getters['order/address']
            )
            if (address) await store.dispatch('order/setAddress', address)
        }
        return
    }

    onNoSelectedRestaurant() {
        if (!this.model.restaurant) {
            this.calculateFinished()
            if (this.calculated) this.calculation = {}
            // eslint-disable-next-line no-console
            console.warn('Calculate error: restaurant not selected!')
            return true
        }
        return false
    }

    onInvalidDeliveryTime(result, temporaryRestaurant) {
        if (!result) return
        if (!temporaryRestaurant) this._deliveryTimeAdjustment = null

        // tasks#168 (D10/D11): a time the restaurant cannot serve for this basket is moved forward by the
        // backend and stays scheduled; adopt it instead of switching to ASAP. Only for the active cart:
        // a temporary calculation (browsing another restaurant) must not reschedule the cart / global time
        if (
            !temporaryRestaurant &&
            result.delivery_time_adjustment === 'moved' &&
            result.required_delivery_time
        ) {
            this.onMovedDeliveryTime(result.required_delivery_time)
            return
        }

        // if the required_delivery_time is invalid and tha order considered as an ASAP order
        if (
            !!this.model.required_delivery_time &&
            !Vue.moment(this.model.required_delivery_time).isSame(
                result.required_delivery_time,
                'minute'
            )
        ) {
            if (!temporaryRestaurant) this._deliveryTimeAdjustment = 'asap'
            store.dispatch('order/setAsap', true)
            store.dispatch('order/resetTime')
        }
    }

    onMovedDeliveryTime(requiredDeliveryTime) {
        const moved = Vue.moment(requiredDeliveryTime)
        if (!moved.isValid()) return

        const alreadySelected =
            !store.getters['order/asap'] && store.getters['order/time'] === moved.valueOf()

        // the order posted right after this calculate (checkout/placeOrder) carries the adopted time
        this.model.required_delivery_time = moved.format()
        store.dispatch('order/setAsap', false)
        store.dispatch('order/setTime', moved.valueOf())

        if (alreadySelected) return

        this._deliveryTimeAdjustment = 'moved'
        // tasks#170: one notice per change. A mounted cart watcher (cartWatcher) tells it: through its "delivery time
        // changed" alert when the change also changed the time inputs, else with the toast below. Without one, the
        // toast is shown right away
        this._movedNotice = { requiredDeliveryTime }
        if (!this._movedNoticeHandlers) this.showMovedNotice(this.takeMovedNotice())
    }

    // tasks#170: cart watchers (several can be mounted at once) register so a "moved" notice is told by one of them
    registerMovedNoticeHandler(delta) {
        this._movedNoticeHandlers = Math.max(0, this._movedNoticeHandlers + delta)
    }

    // tasks#170: the "delivery time changed" alert is mounted only on some pages; elsewhere the toast tells a move
    registerDeliveryTimeAlert(delta) {
        this._deliveryTimeAlerts = Math.max(0, this._deliveryTimeAlerts + delta)
    }

    get deliveryTimeAlertMounted() {
        return this._deliveryTimeAlerts > 0
    }

    // the "moved" notice not told yet (claimed once: the first caller gets it)
    takeMovedNotice() {
        const notice = this._movedNotice
        this._movedNotice = null
        return notice
    }

    showMovedNotice(notice) {
        if (!notice) return
        store.dispatch('toastInfo', {
            title: Vue.i18n.translate('cart.change_alert_time'),
            text: Vue.i18n.translate('checkout.delivery_time_moved', {
                time: Vue.filter('toDeliveryTime')(notice.requiredDeliveryTime),
            }),
        })
    }

    async storeResult(result, temporaryRestaurant) {
        if (temporaryRestaurant) {
            this.temporary = result
        } else if (result) {
            this.calculation = result
            this.inactiveItems = result.validation_errors
            this.overviewCart()
            try {
                await this.overviewCart()
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('Error during cart overview:', e)
            }
        }
    }

    async checkItems() {
        const res = await this._client.store(
            `${this.base}/order/check-item-existence`,
            this._model.items.reduce((p, c) => {
                p.push(mixins.getId(c.meal))
                return p
            }, [])
        )
        let removedItems = []
        if (Object.values(res).includes(false)) {
            this._model.items = this._model.items.filter((item) => {
                if (!res[mixins.getId(item.meal)]) {
                    removedItems.push(item.name)
                }
                return res[mixins.getId(item.meal)]
            })
            store.dispatch('toastInfo', {
                title: Vue.i18n.translate('notfound.deleted_items', undefined, removedItems.length),
                text: Vue.i18n.translate(
                    'notfound.removed_items',
                    { delete: removedItems.join(', ') },
                    removedItems.length
                ),
            })
        }
        return {
            removed: !!removedItems.length,
            items: res,
        }
    }

    async buildCart(order) {
        this._loading = true
        // eslint-disable-next-line no-async-promise-executor

        this.resetMeals()
        this.resetCart()
        await this.resetAllowance()
        const restaurant = new Restaurant(order.restaurant)
        for (const key of Object.keys(prototype)) {
            if (key === 'restaurant') {
                await store.dispatch('order/setRestaurant', restaurant.model)
                this._model.restaurant = restaurant.link
                continue
            }

            if (key === 'delivery_full_address') {
                const address = Vue.prototype.$addresses.items.find((address) =>
                    address.id === order.id
                        ? true
                        : address.name === order.delivery_address_name ||
                          (address.full_address === order.delivery_full_address &&
                              address.sub_address === order.delivery_sub_address)
                )
                if (address) store.dispatch('order/setAddress', address)
                continue
            }

            if (key === 'residentialcanteen_id') {
                if (!order.residentialcanteen_id) continue
                store.dispatch(
                    'active/setCanteen',
                    store.getters['canteen/residentialList'].find(
                        (canteen) => canteen.id === order.residentialcanteen_id
                    )
                )
                continue
            }
            if (key === 'cloudcanteen_id') {
                if (!order.cloudcanteen_id) continue
                store.dispatch(
                    'active/setCanteen',
                    Vue.prototype.$user?.canteens.find(
                        (canteen) => canteen.id === order.cloudcanteen_id
                    )
                )
                continue
            }
            if (key === 'selected_expense_rule') {
                if (!order.selected_expense_rule?.id) continue
                this.selected_expense_rule = order.selected_expense_rule
                await store.dispatch('setAllowance', order.selected_expense_rule)
                continue
            }
            if (key === 'group_order_sum_distribution') {
                if (!order.group_order_employees.length) {
                    Vue.set(this._model, key, [])
                    continue
                }
                continue
            }
            if (key === 'items') {
                order.items.forEach((item) => {
                    let reorderItem = {
                        children: [],
                        meal: `/api/v1/meals/${item.meal.id}`,
                        name: item.name,
                        price: item.price,
                        quantity: item.quantity,
                    }
                    item.children.forEach((child) => {
                        reorderItem.children.push({
                            mealoption: `/api/v1/meal-options/${child.mealoption.id}`,
                            mealoption_id: child.mealoption.id,
                            mealoptiongroup: `/api/v1/meal-option-groups/${child.mealoptiongroup.id}`,
                            mealoptiongroup_id: child.mealoptiongroup.id,
                            name: child.name,
                            price: child.price,
                            quantity: child.quantity,
                        })
                    })
                    this.pushMeal(reorderItem)
                })
                continue
            }
            if (order[key]) Vue.set(this._model, key, order[key])
        }

        await Vue.prototype.$user.getExpenseRules()
        await this.calculate()

        router.push(
            {
                name: 'checkout',
            },
            () => {
                return
            }
        )
    }

    event(event) {
        if (event) this._event.emit(event)
    }

    on(event, listener) {
        if (event && listener) this._event.on(event, listener)
    }

    off(event, listener) {
        if (event && listener) this._event.off(event, listener)
    }
}

export { Cart }
